import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Basic hover containment hook that handles mouse enter/leave events
 */
export const useHoverContainment = (options = {}) => {
  const {
    enterDelay = 0,
    moveDelay = 0,
    leaveDelay = 0,
    containmentCheck = true
  } = options;

  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef(null);
  const timeoutRef = useRef(null);
  const relatedTargetRef = useRef(null);

  const clearHoverTimeout = useCallback(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const isElementInsideContainer = useCallback((element) => {
    if (!containerRef.current || !element) return false;

    const isInsideSummary = element?.closest?.('.user-profile_container');

    // Check if element is inside container or is the container itself
    if (element && element.nodeType === Node.ELEMENT_NODE) {
      return containerRef?.current?.contains?.(element) || containerRef.current === element || isInsideSummary;
    } else {
      return false;
    }
  }, [containerRef]);

  const handleMouseEnter = useCallback((event) => {
    clearHoverTimeout();
    relatedTargetRef.current = event.relatedTarget;

    if (enterDelay > 0) {
      timeoutRef.current = setTimeout(() => {
        setIsHovered(true);
      }, enterDelay);
    } else {
      setIsHovered(true);
    }
  }, [enterDelay, clearHoverTimeout]);

  const handleMouseLeave = useCallback((event) => {
    relatedTargetRef.current = event.relatedTarget;

    // If containment check is enabled, verify if we're actually leaving the container
    if (containmentCheck && isElementInsideContainer(event.relatedTarget)) {
      return; // Still inside the container, don't trigger leave
    }

    clearHoverTimeout();
    
    if (leaveDelay > 0) {
      timeoutRef.current = setTimeout(() => {
        setIsHovered(false);
      }, leaveDelay);
    } else {
      setIsHovered(false);
    }
  }, [leaveDelay, containmentCheck, isElementInsideContainer, clearHoverTimeout]);

  // Additional check for mouse movements inside container
  const handleMouseMove = useCallback((event) => {
    // If we're not hovered but mouse is inside, set hovered
    clearHoverTimeout();

    if (
      !isHovered &&
      containerRef.current &&
      containerRef.current.contains(event.target)
    ) {
      if (moveDelay > 0) {
        timeoutRef.current = setTimeout(() => {
          setIsHovered(true);
        }, moveDelay);
      } else {
        setIsHovered(true);
      }
    }
  }, [isHovered, clearHoverTimeout]);

  // Cleanup
  useEffect(() => {
    return () => clearHoverTimeout();
  }, [clearHoverTimeout]);

  const hoverProps = {
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onMouseMove: containmentCheck ? handleMouseMove : undefined
  };

  return [isHovered, hoverProps, containerRef];
};

/**
 * Enhanced version with callback support
 */
export const useHoverContainmentWithCallback = (onHoverChange, options = {}) => {
  const [isHovered, hoverProps, containerRef] = useHoverContainment(options);

  useEffect(() => {
    onHoverChange?.(isHovered);
  }, [isHovered, onHoverChange]);

  return [isHovered, hoverProps, containerRef];
};


export const useUniversalContainmentHook = (options = {}) => {
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef(null);
  const {
    enterDelay = 0,
    moveDelay = 0,
    leaveDelay = 0,
    onLeave,
    onClick,
    onMove,
    onEnter,
    customContainerRef
  } = options;

  useEffect(() => {
      if (!containerRef?.current && !customContainerRef?.current) return;
      const el = containerRef.current || customContainerRef.current;

      let mouseMoveTimer = null;
      let mouseLeaveTimer = null;
      let lastTagUserId = null;

      const clickHandler = (e) => {
        if (e.target.tagName === 'TAG' || e.target.getAttribute('tag-id')) {
          const tag_user_id = e.target.getAttribute('tag-id');
          onClick?.(tag_user_id, e.target);
        }
      };

      const mouseMoveHandler = (e) => {
        // prefer explicit TAG elements, fallback to any element with tag-id attribute
        const target = e.target;
        const tagEl = target.tagName === 'TAG' ? target : (target.closest ? target.closest('[tag-id]') : null);
        if (!tagEl) return;

        const tag_user_id = tagEl.getAttribute('tag-id');
        if (!tag_user_id) return;

        // cancel any pending hide
        if (mouseLeaveTimer) {
          clearTimeout(mouseLeaveTimer);
          mouseLeaveTimer = null;
        }

        // if same tag shown already, don't re-schedule
        if (lastTagUserId === tag_user_id) return;

        if (mouseMoveTimer) {
          clearTimeout(mouseMoveTimer);
        }


        if(moveDelay > 0){
          mouseMoveTimer = setTimeout(() => {
            onMove?.(tag_user_id, tagEl);
              lastTagUserId = tag_user_id;
              mouseMoveTimer = null;
          }, moveDelay);
        } else {
          onMove?.(tag_user_id, tagEl);
          lastTagUserId = tag_user_id;  
        }
      };

      const mouseEnterHandler = (e) => {
        const target = e.target;
        const tagEl = target.tagName === 'TAG' ? target : (target.closest ? target.closest('[tag-id]') : null);
        if (!tagEl) return;

        const tag_user_id = tagEl.getAttribute('tag-id');
        if (!tag_user_id) return;

        // cancel any pending hide
        if (mouseLeaveTimer) {
          clearTimeout(mouseLeaveTimer);
          mouseLeaveTimer = null;
        }

        // if same tag shown already, don't re-schedule
        if (lastTagUserId === tag_user_id) return;

        if (mouseMoveTimer) {
          clearTimeout(mouseMoveTimer);
          mouseMoveTimer = null;
        }

        if (enterDelay > 0) {
          mouseMoveTimer = setTimeout(() => {
            onEnter?.(tag_user_id, tagEl);
            lastTagUserId = tag_user_id;
            mouseMoveTimer = null;
          }, enterDelay);
        } else {
          onEnter?.(tag_user_id, tagEl);
          lastTagUserId = tag_user_id;
        }
      };

      const mouseLeaveHandler = (e) => {
        // if moving into another tag element, keep it open
        const related = e.relatedTarget;

        const isInsideSummary = related?.closest?.('.user-profile_container');
        if (related && (related.tagName === 'TAG' || (related.getAttribute && related.getAttribute('tag-id')))) return;

        if (isInsideSummary) return;

        // cancel pending show
        if (mouseMoveTimer) {
          clearTimeout(mouseMoveTimer);
          mouseMoveTimer = null;
        }

        if (mouseLeaveTimer) {
          clearTimeout(mouseLeaveTimer);
        }

        if(leaveDelay > 0) {
          mouseLeaveTimer = setTimeout(() => {
            onLeave?.(lastTagUserId);
            lastTagUserId = null;
            mouseLeaveTimer = null;
          }, leaveDelay);
        } else {
          onLeave?.(lastTagUserId);
          lastTagUserId = null;
        }
      };

      el.addEventListener('click', clickHandler, true);
      el.addEventListener('mousemove', mouseMoveHandler, true);
      el.addEventListener('mouseleave', mouseLeaveHandler, true);
      el.addEventListener('mouseenter', mouseEnterHandler, true);

      // cleanup for the newly added listeners and timers
      return () => {
        el.removeEventListener('mousemove', mouseMoveHandler, true);
        el.removeEventListener('mouseleave', mouseLeaveHandler, true);
        el.removeEventListener('mouseenter', mouseEnterHandler, true);
        el.removeEventListener('click', clickHandler, true);

        if (mouseMoveTimer) {
          clearTimeout(mouseMoveTimer);
          mouseMoveTimer = null;
        }
        if (mouseLeaveTimer) {
          clearTimeout(mouseLeaveTimer);
          mouseLeaveTimer = null;
        }
      };
    }, [containerRef, onEnter, onLeave, onMove]);


    return [ containerRef ];
};